import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import { useState, useCallback } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { useAuthLogin } from '@/api/auth'
import { Leaf, User, Key, ArrowRight, ChevronLeft, Eye, EyeOff } from 'lucide-react'

export const Route = createFileRoute('/auth')({
  beforeLoad: () => {
    const { backendUrl, sessionToken } = useSessionStore.getState()
    if (!backendUrl) {
      throw redirect({ to: '/connect' })
    }
    // Already have a session — go to store selection. /select-store
    // auto-forwards to /select-register when the manager only has
    // access to one store, so single-store accounts keep the
    // one-click flow.
    if (sessionToken) {
      throw redirect({ to: '/select-store' })
    }
  },
  component: AuthPage,
})

type AuthTab = 'manager' | 'store'

function AuthPage() {
  const navigate = useNavigate()
  const { backendUrl } = useSessionStore()
  const loginMutation = useAuthLogin()

  const [activeTab, setActiveTab] = useState<AuthTab>('manager')
  const [error, setError] = useState<string | null>(null)

  // Manager fields
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Store fields
  const [apiKey, setApiKey] = useState('')
  const [apiPassword, setApiPassword] = useState('')
  const [showApiPassword, setShowApiPassword] = useState(false)

  const handleSubmit = useCallback(async () => {
    setError(null)

    try {
      if (activeTab === 'manager') {
        if (!username.trim() || !password) {
          setError('Username and password are required')
          return
        }
        await loginMutation.mutateAsync({
          auth_type: 'manager',
          username: username.trim(),
          password,
        })
      } else {
        if (!apiKey.trim() || !apiPassword) {
          setError('API key and password are required')
          return
        }
        await loginMutation.mutateAsync({
          auth_type: 'store',
          api_key: apiKey.trim(),
          api_password: apiPassword,
        })
      }

      // Both auth types land on /select-store. Manager logins with
      // multi-store access render the picker; single-store managers
      // and API-key (store-scoped) logins auto-skip forward to
      // /select-register.
      navigate({ to: '/select-store' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.')
    }
  }, [activeTab, username, password, apiKey, apiPassword, loginMutation, navigate])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }, [handleSubmit])

  const handleBack = () => {
    navigate({ to: '/connect' })
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-green-600 to-green-800 p-8">
      {/* Back button */}
      <button
        onClick={handleBack}
        className="absolute top-6 left-6 flex items-center gap-1 text-white/80 hover:text-white transition-colors cursor-pointer"
      >
        <ChevronLeft className="w-5 h-5" />
        <span className="text-sm font-medium">Change Subdomain</span>
      </button>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur rounded-2xl mb-4">
            <Leaf className="w-10 h-10 text-white" />
          </div>
          {/* <h1 className="text-2xl font-bold text-white">WAIO POS</h1> */}
          <p className="text-green-100 mt-2">Sign in to your store</p>
          <p className="text-white/60 text-xs mt-1">
            {backendUrl?.replace('https://', '').replace('http://', '')}
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          {/* Tabs */}
          <div className="flex mb-6 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => { setActiveTab('manager'); setError(null) }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'manager'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <User className="w-4 h-4" />
              Manager
            </button>
            <button
              onClick={() => { setActiveTab('store'); setError(null) }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'store'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Key className="w-4 h-4" />
              Register
            </button>
          </div>

          {/* Manager Login Form */}
          {activeTab === 'manager' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setError(null) }}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter username"
                  className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null) }}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter password"
                    className="w-full h-12 px-4 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Store Login Form */}
          {activeTab === 'store' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  API Key
                </label>
                <input
                  type="text"
                  value={apiKey}
                  onChange={(e) => { setApiKey(e.target.value); setError(null) }}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter API key"
                  className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  API Password
                </label>
                <div className="relative">
                  <input
                    type={showApiPassword ? 'text' : 'password'}
                    value={apiPassword}
                    onChange={(e) => { setApiPassword(e.target.value); setError(null) }}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter API password"
                    className="w-full h-12 px-4 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiPassword(!showApiPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    {showApiPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loginMutation.isPending}
            className="w-full mt-6 h-12 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors cursor-pointer"
          >
            {loginMutation.isPending ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Sign In
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-green-100/60 text-sm mt-6">
          v2.0.0
        </p>
      </div>
    </div>
  )
}
